import { STAGE_WEIGHTS, resolveScore } from '@/lib/reportConstants';
import { panelScoreToOn5 } from '@/lib/format';

/** Uniform 1-decimal formatting for all /5 scores in Evaluation Summary. */
export function formatOn5(score) {
  if (score == null || Number.isNaN(Number(score))) return null;
  return Number(score).toFixed(1);
}

/** Strip markdown and return the first ~2 sentences for consolidated summary. */
export function briefNarrative(narrative) {
  if (!narrative) return '';
  const plain = narrative.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
  const sentences = plain.match(/[^.!?]+[.!?]+/g) || [plain];
  const brief = sentences.slice(0, 2).join(' ').trim();
  if (brief.length <= 280) return brief;
  return `${brief.slice(0, 277).trim()}…`;
}

/**
 * Legacy equal-weight overall (kept for any callers). Prefer computeWeightedSummary.
 */
export function computeOverallMetrics(data) {
  const summary = computeWeightedSummary(data);
  if (!summary.rows.length) {
    return { overallPct: null, rating: 'Pending', stageCount: 0 };
  }
  const overallPct = summary.compositePct;
  let rating = 'Weak';
  if (overallPct >= 80) rating = 'Strong';
  else if (overallPct >= 65) rating = 'Good';
  else if (overallPct >= 50) rating = 'Neutral';
  return { overallPct, rating, stageCount: summary.rows.length };
}

function avgOn5(values) {
  const nums = values.filter((v) => v != null && !Number.isNaN(Number(v))).map(Number);
  if (!nums.length) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
}

function appendHrRow(rows, hr) {
  if (!hr || rows.some((r) => r.key === 'hr')) return rows;
  if (hr.overall_score == null && !(hr.factors && hr.factors.length)) return rows;

  const overall = resolveScore(hr.human_overall_score, hr.overall_score);
  const factorAvg = hr.factors?.length
    ? avgOn5(hr.factors.map((f) => resolveScore(f.human_score, f.score)))
    : null;
  const score = overall ?? factorAvg;
  if (score == null) return rows;

  const n = hr.factors?.length || 6;
  return [
    ...rows,
    {
      key: 'hr',
      stage: 'HR Evaluation',
      raw: `Average of ${n} rated factors`,
      scoreLabel: `${formatOn5(score)} / 5`,
      norm: Number(score) / 5,
      baseWeight: STAGE_WEIGHTS.hr,
    },
  ];
}

/** Build summary rows from rendered magazine sections (preferred — honours human overrides). */
function buildWeightedRowsFromSections(data) {
  const rows = [];

  const curated = data.videobot?.responses || [];
  if (curated.length) {
    const scores = curated.map((r) => resolveScore(r.human_overall_score, r.overall_score));
    const avg = avgOn5(scores);
    if (avg != null) {
      rows.push({
        key: 'videobot',
        stage: 'Video Bot Screening',
        raw: `${curated.length} of ${curated.length} questions answered`,
        scoreLabel: `${formatOn5(avg)} / 5 avg`,
        norm: avg / 5,
        baseWeight: STAGE_WEIGHTS.videobot,
      });
    }
  }

  const mcq = data.mcq?.result;
  if (mcq?.score_pct != null) {
    rows.push({
      key: 'iq',
      stage: 'IQ Assessment',
      raw: `${mcq.correct ?? 0} of ${mcq.total_questions ?? 0} correct`,
      scoreLabel: `${mcq.score_pct}%`,
      norm: mcq.score_pct / 100,
      baseWeight: STAGE_WEIGHTS.iq,
    });
  }

  const tech = data.tech_assessment?.result;
  if (tech?.score_pct != null) {
    rows.push({
      key: 'work',
      stage: 'Work Assessment',
      raw: `${tech.correct ?? 0} of ${tech.total_questions ?? 0} correct`,
      scoreLabel: `${tech.score_pct}%`,
      norm: tech.score_pct / 100,
      baseWeight: STAGE_WEIGHTS.work,
    });
  }

  const panels = (data.panels || []).filter(
    (p) => p.human_overall_score != null
      || (Array.isArray(p.factor_scores) && p.factor_scores.length),
  );
  if (panels.length) {
    const panelScores = panels.map((p) => p.human_overall_score);
    const avg = avgOn5(panelScores);
    if (avg != null) {
      const factorCount = (panels[0]?.factor_scores || []).length || 6;
      rows.push({
        key: 'panel',
        stage: 'Panel Evaluation',
        raw: `Average of ${factorCount} rated factors`,
        scoreLabel: `${formatOn5(avg)} / 5`,
        norm: avg / 5,
        baseWeight: STAGE_WEIGHTS.panel,
      });
    }
  }

  return appendHrRow(rows, data.hr_evaluation);
}

/**
 * Fallback when individual stage sections were withheld but the Evaluation
 * Summary (`final_report`) artefact is included — bind to backend stage blocks.
 */
function buildWeightedRowsFromFinalReportStages(stages) {
  const rows = [];
  const panelScores = [];

  for (const block of stages || []) {
    const code = block?.stage;
    const score = block?.overall_score;
    if (score == null || Number.isNaN(Number(score))) continue;

    if (code === 'VIDEOBOT') {
      const on5 = Number(score) / 20;
      const count = block.responses_evaluated ?? block.response_count ?? 0;
      rows.push({
        key: 'videobot',
        stage: 'Video Bot Screening',
        raw: count ? `${count} question${count === 1 ? '' : 's'} evaluated` : 'Video responses evaluated',
        scoreLabel: `${formatOn5(on5)} / 5 avg`,
        norm: on5 / 5,
        baseWeight: STAGE_WEIGHTS.videobot,
      });
    } else if (code === 'MCQ') {
      const pct = Number(score);
      rows.push({
        key: 'iq',
        stage: block.label || 'IQ Assessment',
        raw: block.eval_summary?.trim() || 'Scored assessment',
        scoreLabel: `${pct}%`,
        norm: pct / 100,
        baseWeight: STAGE_WEIGHTS.iq,
      });
    } else if (code === 'TECH_ASSESSMENT') {
      const pct = Number(score);
      rows.push({
        key: 'work',
        stage: 'Work Assessment',
        raw: block.eval_summary?.trim() || 'Scored assessment',
        scoreLabel: `${pct}%`,
        norm: pct / 100,
        baseWeight: STAGE_WEIGHTS.work,
      });
    } else if (code === 'PANEL') {
      panelScores.push(panelScoreToOn5(score));
    }
  }

  if (panelScores.length) {
    const avg = avgOn5(panelScores);
    if (avg != null) {
      rows.push({
        key: 'panel',
        stage: 'Panel Evaluation',
        raw: 'Average of 6 rated factors',
        scoreLabel: `${formatOn5(avg)} / 5`,
        norm: avg / 5,
        baseWeight: STAGE_WEIGHTS.panel,
      });
    }
  }

  return rows;
}

function finalizeWeightedRows(rows) {
  if (!rows.length) {
    return { rows: [], compositePct: null, formula: '', weightSum: 0 };
  }

  const weightSum = rows.reduce((a, r) => a + r.baseWeight, 0);
  const weighted = rows.map((r) => {
    const weightPct = Math.round((r.baseWeight / weightSum) * 1000) / 10;
    return { ...r, weightPct, contribution: r.norm * (r.baseWeight / weightSum) };
  });

  const compositePct = Math.round(weighted.reduce((a, r) => a + r.contribution, 0) * 100);

  const formulaParts = weighted.map((r) => {
    if (r.key === 'iq' || r.key === 'work') {
      return `(${Math.round(r.norm * 100)}%×${r.weightPct}%)`;
    }
    return `(${formatOn5(r.norm * 5)}/5×${r.weightPct}%)`;
  });
  const formula = `${formulaParts.join(' + ')} = ${compositePct}%`;

  return { rows: weighted, compositePct, formula, weightSum };
}

/**
 * Weighted Evaluation Summary matching hireiq-report-mock.html.
 * Missing stages are omitted; present-stage weights are renormalized proportionally.
 *
 * Prefers live magazine section data (human overrides). When the Evaluation
 * Summary artefact is included but stage sections were withheld, falls back to
 * `final_report.stages` from the API.
 */
export function computeWeightedSummary(data) {
  let rows = buildWeightedRowsFromSections(data);
  if (!rows.length && data.final_report?.stages?.length) {
    rows = buildWeightedRowsFromFinalReportStages(data.final_report.stages);
    rows = appendHrRow(rows, data.hr_evaluation);
  }
  return finalizeWeightedRows(rows);
}
